import type { HouseConfig } from '@/model/schema';
import type { GenerationConstraints } from '@/generator/constraints';

/**
 * Shared export metadata payload.
 *
 * Every exporter (USDA, GLB, any future format) writes these same
 * top-level fields so downstream pipelines can attribute and re-run a
 * generation from the artifact alone. The shape is intentionally JSON
 * friendly: string values, flat where possible, nested only when the
 * field already has internal structure (e.g. `settings`).
 */
export interface ExportMetadata {
  /** App + build identifier. Written as "structure v<version>". */
  generator: string;
  /** ISO-8601 UTC timestamp of the export. */
  generatedAt: string;
  /** Canonical HouseConfig seed. */
  seed: string;
  /** Chosen style preset (modern, suburban, ...). */
  preset: string;
  /** Generator score 0..1 (quality of the chosen candidate). */
  score: number;
  /** Top-level generative constraints as JSON-friendly object. */
  settings: Record<string, unknown>;
  /** Derived structural summary (story count, footprint, etc.). */
  summary: Record<string, unknown>;
  /** Free-form user-provided site address. Empty string if unset. */
  siteAddress: string;
  /** Free-form user-provided description. Empty string if unset. */
  siteDescription: string;
  /** Schema version of the source HouseConfig. Lets a reader know
   *  whether additional fields may have been dropped in this export. */
  schemaVersion: number;
  /** Texture set IDs chosen by the generator (per material category).
   *  Empty object if the export uses only flat colors. Downstream
   *  pipelines look these up under `public/textures/<cat>/<id>/`. */
  textures: Record<string, string>;
}

/**
 * Application version — written verbatim into the `generator` string.
 * Update when a user-observable change would help consumers of the
 * exported file differentiate pipelines.
 */
export const APP_VERSION = '0.4.0';

export interface BuildMetadataInput {
  config: HouseConfig;
  constraints: GenerationConstraints | null;
  siteAddress: string;
  siteDescription: string;
}

export function buildExportMetadata(input: BuildMetadataInput): ExportMetadata {
  const { config } = input;
  const storyCount = config.stories.length;
  const footprint = `${config.house.footprintWidth.toFixed(2)} m × ${config.house.footprintDepth.toFixed(2)} m`;
  return {
    generator: `structure v${APP_VERSION}`,
    generatedAt: new Date().toISOString(),
    seed: config.seed,
    preset: config.style.preset,
    score: config.score,
    settings: serialiseConstraints(input.constraints),
    summary: {
      storyCount,
      footprint,
      footprintWidth: round(config.house.footprintWidth, 3),
      footprintDepth: round(config.house.footprintDepth, 3),
      storyHeight: round(config.house.storyHeight, 3),
      roofType: config.roof.type,
      hasFence: Boolean(config.fence),
      hasMailbox: Boolean(config.mailbox),
      hasPlinth: config.house.plinthHeight > 1e-3,
      plinthHeight: round(config.house.plinthHeight, 3),
      deckCount: config.decks.length,
      balconyCount: config.stories.filter((s) => s.balcony).length,
      lotWidth: round(config.lot.width, 3),
      lotDepth: round(config.lot.depth, 3),
    },
    siteAddress: input.siteAddress,
    siteDescription: input.siteDescription,
    schemaVersion: config.schemaVersion,
    textures: textureSelectionsToRecord(config.textures),
  };
}

function textureSelectionsToRecord(
  selections: HouseConfig['textures'],
): Record<string, string> {
  if (!selections) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(selections)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

function round(n: number, digits: number): number {
  const p = Math.pow(10, digits);
  return Math.round(n * p) / p;
}

function serialiseConstraints(c: GenerationConstraints | null): Record<string, unknown> {
  if (!c) return {};
  // Pull the "value" out of every wrapped constraint so the export is
  // immediately human-readable; the lock bits are internal UX state.
  const out: Record<string, unknown> = { seed: c.seed };
  for (const [key, value] of Object.entries(c)) {
    if (key === 'seed') continue;
    if (value && typeof value === 'object') {
      const v = value as { value?: unknown; min?: number; max?: number };
      if ('min' in v && 'max' in v) {
        out[key] = { min: v.min, max: v.max };
      } else if ('value' in v) {
        out[key] = v.value;
      }
    }
  }
  return out;
}
